import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/Button';
import { NodeMultiSelect } from '@/components/NodeMultiSelect';
import { InheritanceSourceBadge, type InheritanceSourceKind } from '@/components/InheritanceSourceBadge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DIRECTION_CONSTRAINT_LABELS, OVERRIDE_MODE_LABELS, OVERRIDE_PERSISTENCE_LABELS, SETTING_LABELS,
} from '@/components/conditions/labels';
import type { NodesResponse } from '@/stores/scope';

/** `/settings/inheritance` (v0.4 章②、D-58/D-59/D-69/D-114)。
 *  設定キー一覧はカタログから自動生成し、テナントのポリシー (上書き可否・方向・承認要否・
 *  上書き可能レベル・持続性) を編集する。実際の値そのもの (`node_scalar_settings` 等の行) は
 *  消費機能側 (章④〜⑥) が編集し、ここでは「出所ノードを確認するプレビュー」までを提供する。
 *  設計: docs/design/v04-shift-labor-management-detail.md §2-8 */

type InheritablePolicy = {
  override_mode: 'inherit_only' | 'override' | 'constrained';
  direction_constraint: 'none' | 'gte_parent' | 'lte_parent';
  overridable_from_node_type_id: string | null;
  requires_approval: boolean;
  override_persistence: 'once' | 'persistent' | 'until_reassignment';
};

type InheritableSetting = {
  setting_key: string;
  label_key: string;
  inheritance_semantics: 'value_inheritance' | 'template_only' | 'shared_definition_ref';
  requires_explicit_setting: boolean;
  axis: 'node' | 'node_and_person';
  storage_table: string;
  value_shape: 'scalar' | 'row_set' | 'reference';
  is_implemented: boolean;
  policy: InheritablePolicy;
};

type NodeType = { node_type_id: string; code: string; label: string; sort_order: number };

type ResolveResult = {
  setting_key: string;
  node_id: string;
  value: unknown;
  source_node_id: string | null;
  source_kind: InheritanceSourceKind;
  source_node_label: string | null;
  policy: InheritablePolicy;
};

const NONE_NODE_TYPE = '__none__';

function PolicySummaryBadges({ policy, isImplemented }: { policy: InheritablePolicy; isImplemented: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
        {OVERRIDE_MODE_LABELS[policy.override_mode]}
      </span>
      {policy.direction_constraint !== 'none' && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
          {DIRECTION_CONSTRAINT_LABELS[policy.direction_constraint]}
        </span>
      )}
      {policy.requires_approval && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-alert-bg text-alert-text border border-alert-border whitespace-nowrap">
          承認必須
        </span>
      )}
      {!isImplemented && (
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-surface-sunken text-text-sub border border-surface-border whitespace-nowrap">
          未実装 (今後の章で有効化)
        </span>
      )}
    </div>
  );
}

function SettingPolicyPanel({ setting, nodeTypes, onSaved }: {
  setting: InheritableSetting;
  nodeTypes: NodeType[];
  onSaved: () => void;
}) {
  const [form, setForm] = useState<InheritablePolicy>(() => ({ ...setting.policy }));
  const isDirty = JSON.stringify(form) !== JSON.stringify(setting.policy);

  const saveMut = useMutation({
    mutationFn: () => apiFetch(`/inheritable-settings/${encodeURIComponent(setting.setting_key)}/policy`, {
      method: 'PUT',
      body: JSON.stringify(form),
    }),
    onSuccess: () => { onSaved(); toast.success('ポリシーを保存しました'); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '保存に失敗しました (全店舗スコープの管理権限が必要です)'),
  });

  return (
    <div className="space-y-3 pt-3 border-t border-surface-border">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs text-text-sub mb-1">上書き可否</label>
          <Select value={form.override_mode} onValueChange={(v) => setForm({ ...form, override_mode: v as InheritablePolicy['override_mode'] })}>
            <SelectTrigger aria-label="上書き可否"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(OVERRIDE_MODE_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">上書き方向の制約</label>
          <Select value={form.direction_constraint} onValueChange={(v) => setForm({ ...form, direction_constraint: v as InheritablePolicy['direction_constraint'] })}>
            <SelectTrigger aria-label="上書き方向の制約"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(DIRECTION_CONSTRAINT_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">上書きを許可する階層レベル</label>
          <Select
            value={form.overridable_from_node_type_id ?? NONE_NODE_TYPE}
            onValueChange={(v) => setForm({ ...form, overridable_from_node_type_id: v === NONE_NODE_TYPE ? null : v })}
          >
            <SelectTrigger aria-label="上書きを許可する階層レベル"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE_NODE_TYPE}>制限なし (どの階層でも上書き可)</SelectItem>
              {nodeTypes.map((nt) => <SelectItem key={nt.node_type_id} value={nt.node_type_id}>{nt.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="block text-xs text-text-sub mb-1">上書きの持続性</label>
          <Select value={form.override_persistence} onValueChange={(v) => setForm({ ...form, override_persistence: v as InheritablePolicy['override_persistence'] })}>
            <SelectTrigger aria-label="上書きの持続性"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(OVERRIDE_PERSISTENCE_LABELS).map(([k, label]) => <SelectItem key={k} value={k}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm min-h-[44px]">
        <input type="checkbox" className="w-5 h-5" checked={form.requires_approval}
          onChange={(e) => setForm({ ...form, requires_approval: e.target.checked })} />
        上書きに承認を必須にする
      </label>
      <div className="flex justify-end gap-2">
        {isDirty && <Button variant="ghost" size="sm" onClick={() => setForm({ ...setting.policy })}>破棄</Button>}
        <Button variant="primary" size="sm" disabled={!isDirty || saveMut.isPending} loading={saveMut.isPending} onClick={() => saveMut.mutate()}>
          保存
        </Button>
      </div>
    </div>
  );
}

export function InheritanceSettingsPage() {
  const qc = useQueryClient();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewNodeId, setPreviewNodeId] = useState<string | null>(null);
  const [previewResult, setPreviewResult] = useState<ResolveResult | null>(null);

  const { data, isLoading } = useQuery<{ settings: InheritableSetting[] }>({
    queryKey: ['inheritable-settings'],
    queryFn: () => apiFetch<{ settings: InheritableSetting[] }>('/inheritable-settings'),
  });
  const { data: nodeTypes = [] } = useQuery<NodeType[]>({
    queryKey: ['node-types'],
    queryFn: () => apiFetch<NodeType[]>('/node-types'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: nodesData } = useQuery<NodesResponse>({
    queryKey: ['nodes'],
    queryFn: () => apiFetch<NodesResponse>('/nodes'),
    staleTime: 5 * 60 * 1000,
  });

  const settings = data?.settings ?? [];
  const implementedSettings = settings.filter((s) => s.is_implemented);

  const resolveMut = useMutation({
    mutationFn: () => apiFetch<ResolveResult>(
      `/inheritable-settings/${encodeURIComponent(previewKey!)}/resolve?node_id=${encodeURIComponent(previewNodeId!)}`,
    ),
    onSuccess: (r) => setPreviewResult(r),
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '解決に失敗しました'),
  });

  const handleSingleNodeChange = (next: Set<string>) => {
    if (next.size === 0) { setPreviewNodeId(null); return; }
    const arr = [...next];
    const newOne = arr.find((id) => id !== previewNodeId);
    setPreviewNodeId(newOne ?? arr[0]!);
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-h2">階層継承設定</h2>
        <p className="text-sm text-text-sub mt-1">
          店舗・ブランド等の組織階層ごとに値を上書きできる設定について、上書きの可否・方向・承認要否を管理します。
        </p>
      </div>

      {/* 出所を確認するプレビュー */}
      <section className="border border-surface-border rounded-lg bg-white p-4 space-y-3">
        <h3 className="text-sm font-semibold text-text-body">出所を確認する</h3>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 sm:items-end">
          <div className="min-w-0">
            <label className="block text-xs text-text-sub mb-1">設定キー</label>
            <Select value={previewKey ?? ''} onValueChange={(v) => setPreviewKey(v || null)}>
              <SelectTrigger aria-label="設定キー"><SelectValue placeholder="選択してください" /></SelectTrigger>
              <SelectContent>
                {implementedSettings.map((s) => (
                  <SelectItem key={s.setting_key} value={s.setting_key}>{SETTING_LABELS[s.setting_key] ?? s.setting_key}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0">
            <label className="block text-xs text-text-sub mb-1">対象ノード</label>
            <NodeMultiSelect
              nodes={nodesData?.roots ?? []}
              selectedIds={previewNodeId ? new Set([previewNodeId]) : new Set()}
              onChange={handleSingleNodeChange}
            />
          </div>
          <Button variant="secondary" size="sm" disabled={!previewKey || !previewNodeId || resolveMut.isPending}
            loading={resolveMut.isPending} onClick={() => resolveMut.mutate()}>
            解決する
          </Button>
        </div>
        {previewResult && (
          <div className="rounded border border-surface-border bg-surface-sunken p-3 space-y-2">
            <InheritanceSourceBadge sourceKind={previewResult.source_kind} sourceNodeLabel={previewResult.source_node_label} />
            <p className="text-sm text-text-body break-words">
              値: <span className="font-mono text-xs">{previewResult.value === null || previewResult.value === undefined ? '(未設定)' : JSON.stringify(previewResult.value)}</span>
            </p>
          </div>
        )}
      </section>

      {/* 設定キー一覧 */}
      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : (
        <div className="space-y-2">
          {settings.map((s) => {
            const expanded = expandedKey === s.setting_key;
            return (
              <div key={s.setting_key} className="border border-surface-border rounded-lg bg-white">
                <button
                  type="button"
                  onClick={() => setExpandedKey(expanded ? null : s.setting_key)}
                  aria-expanded={expanded}
                  className="w-full flex items-start justify-between gap-2 px-3 sm:px-4 py-3 min-h-[44px] text-left"
                >
                  <span className="min-w-0 flex-1 space-y-1">
                    <span className="block text-sm font-semibold text-text-body break-words">
                      {SETTING_LABELS[s.setting_key] ?? s.setting_key}
                    </span>
                    <PolicySummaryBadges policy={s.policy} isImplemented={s.is_implemented} />
                  </span>
                  <ChevronDown size={16} aria-hidden className={`shrink-0 mt-1 text-text-sub transition-transform ${expanded ? 'rotate-180' : ''}`} />
                </button>
                {expanded && (
                  <div className="px-3 sm:px-4 pb-4">
                    <SettingPolicyPanel setting={s} nodeTypes={nodeTypes}
                      onSaved={() => qc.invalidateQueries({ queryKey: ['inheritable-settings'] })} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
